import {CommonModule} from "@angular/common"
import {Component, computed, inject, Injector, input, OnInit, signal, ChangeDetectionStrategy} from "@angular/core"
import {outputToObservable, takeUntilDestroyed} from "@angular/core/rxjs-interop"
import {MatDialogRef} from "@angular/material/dialog"
import {MatMenuModule} from "@angular/material/menu"
import {MatTooltipModule} from "@angular/material/tooltip"
import {RouterModule} from "@angular/router"
import {createLinkAddTemplateRevisionFromPictures, createLinkToEditorFromPictures} from "@app/common/helpers/routes"
import {IsDefined} from "@cm/utils/filter"
import {CardErrorComponent, CardPlaceholderComponent} from "@common/components/cards"
import {CommentBoxesComponent} from "@common/components/comment-boxes/comment-boxes.component"
import {DialogRenameComponent} from "@common/components/dialogs/dialog-rename/dialog-rename.component"
import {DialogComponent} from "@common/components/dialogs/dialog/dialog.component"
import {EntityCardComponent} from "@common/components/entity/entity-card/entity-card.component"
import {DraggableItemListComponent, ListInfoComponent} from "@common/components/item"
import {InfiniteListComponent} from "@common/components/lists"
import {fetchThrowingErrors} from "@common/helpers/api/fetch"
import {mutateThrowingErrors} from "@common/helpers/api/mutate"
import {BatchDownloadData, BatchUpdateProperty} from "@common/models/item/list-item"
import {SiloService} from "@common/services/auth/silo.service"
import {Enums} from "@enums"
import {
    ContentTypeModel,
    DataObjectAssignmentType,
    MutationUpdatePictureInput,
    OrganizationType,
    PaymentState,
    PictureOrderByCriteria,
    PictureState,
    SortOrder,
    TagType,
    TemplateState,
    TemplateType,
} from "@generated"
import {UploadServiceDataObjectFragment} from "@common/services/upload/upload.generated"
import {Labels, StateLabel} from "@labels"
import {UtilsService} from "@legacy/helpers/utils"
import {Action, MoveCopyPictureDialogResult, MovePictureDialogComponent} from "@platform/components/pictures/move-picture-dialog/move-picture-dialog.component"
import {
    PicturesGridBatchUpdatePicturesGQL,
    PicturesGridCreatePictureRevisionGQL,
    PicturesGridCreateTemplateGQL,
    PicturesGridDeletePictureGQL,
    PicturesGridItemFragment,
    PicturesGridItemsGQL,
    PicturesGridItemsQuery,
    PicturesGridPictureDataGQL,
    PicturesGridUpdateDataObjectGQL,
    PicturesGridUpdatePictureGQL,
    PicturesGridVisibleUsersGQL,
    CreateTemplateRevisionGQL,
    GetTemplateWithLatestRevisionGQL,
    PicturesGridItemsQueryVariables,
    PicturesGridCreatePictureGQL,
} from "@platform/components/pictures/pictures-grid/pictures-grid.generated"
import {PaymentStateLabelComponent} from "@platform/components/shared/payment-state-label/payment-state-label.component"
import {StateLabelComponent} from "@platform/components/shared/state-label/state-label.component"
import {TagLabelsComponent} from "@platform/components/tags/tag-labels/tag-labels.component"
import {AssignUserDialogComponent} from "@platform/components/users/assign-user-dialog/assign-user-dialog.component"
import {AssignUserComponent} from "@platform/components/users/assign-user/assign-user.component"
import {CancellableRequest} from "@platform/models/data/cancellable-request"
import {firstValueFrom, map, of, Subscription} from "rxjs"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        AssignUserDialogComponent,
        AssignUserComponent,
        CardErrorComponent,
        CardPlaceholderComponent,
        CommentBoxesComponent,
        CommonModule,
        EntityCardComponent,
        InfiniteListComponent,
        ListInfoComponent,
        MatMenuModule,
        MatTooltipModule,
        RouterModule,
        StateLabelComponent,
        PaymentStateLabelComponent,
        TagLabelsComponent,
    ],
    selector: "cm-pictures-grid",
    styleUrls: ["./pictures-grid.component.scss"],
    templateUrl: "./pictures-grid.component.html",
})
export class PicturesGridComponent extends DraggableItemListComponent<PicturesGridItemFragment, MutationUpdatePictureInput> implements OnInit {
    readonly silo = inject(SiloService)

    readonly $stateLabels = signal<StateLabel<PictureState>[]>(Array.from(Labels.PictureState.values()))
    readonly $batchDownloadData = signal<BatchDownloadData<PicturesGridItemFragment>[]>([])

    readonly $selectedTab = input.required<"projects" | "filters">({alias: "selectedTab"})
    readonly $organization = computed<{id: string} | null | undefined>(() => undefined)

    public readonly utils = inject(UtilsService)

    readonly visibleUsersGql = inject(PicturesGridVisibleUsersGQL)
    readonly pictureDataGql = inject(PicturesGridPictureDataGQL)
    readonly updatePictureGql = inject(PicturesGridUpdatePictureGQL)
    readonly createPictureGql = inject(PicturesGridCreatePictureGQL)
    readonly updateDataObjectGql = inject(PicturesGridUpdateDataObjectGQL)
    readonly createTemplateGql = inject(PicturesGridCreateTemplateGQL)
    readonly templateWithLatestRevisionGql = inject(GetTemplateWithLatestRevisionGQL)
    readonly createTemplateRevisionGql = inject(CreateTemplateRevisionGQL)
    readonly deletePictureGql = inject(PicturesGridDeletePictureGQL)
    readonly createPictureRevisionGql = inject(PicturesGridCreatePictureRevisionGQL)
    readonly updatePicturesGql = inject(PicturesGridBatchUpdatePicturesGQL)
    readonly itemsGql = inject(PicturesGridItemsGQL)
    readonly picturesItemsGql = inject(PicturesGridItemsGQL)
    readonly injector = inject(Injector)
    readonly OrganizationType = OrganizationType

    dataObjectUpdateSubscriptionsMap = new Map<string, Subscription>()
    visibleUsers: StateLabel<string>[] | null = null

    private fetchRequest = new CancellableRequest<PicturesGridItemsQuery, PicturesGridItemsQueryVariables>(
        this.picturesItemsGql,
        this.injector,
        this.destroyRef,
    )

    override ngOnInit() {
        super.ngOnInit()

        if (this.$can().read.menu("batchDownloadPictures")) this.$batchDownloadData.set(this.getBatchDownloadData())

        if (this.auth.isStaff()) {
            fetchThrowingErrors(this.visibleUsersGql)({}).then(({users}) => {
                this.visibleUsers = users.filter(IsDefined).map((user) => ({
                    label: user.name,
                    state: user.id,
                }))
            })
        }
    }

    getBatchDownloadData = (): BatchDownloadData<PicturesGridItemFragment>[] => {
        const imageExport = {
            pathInMenu: "Pictures",
            getDataObjectsForDownload: async (pictureListItem: PicturesGridItemFragment) => {
                const latestRevisionId = pictureListItem.latestRevision?.id
                if (!latestRevisionId) {
                    throw new Error("No latest revision found for picture")
                }
                const {
                    pictureRevision: {pictureData},
                } = await fetchThrowingErrors(this.pictureDataGql)({id: latestRevisionId})
                const pictureDataItem = pictureData?.[0]?.dataObject
                if (!pictureDataItem) throw new Error("No data object found for picture")
                return [{path: pictureDataItem.originalFileName, dataObjectLegacyId: pictureDataItem.legacyId}]
            },
        }

        return [imageExport]
    }

    // OVERLOADS

    protected override _contentTypeModel = ContentTypeModel.Picture
    protected override _batchUpdate = (property: BatchUpdateProperty, value: string | boolean) =>
        mutateThrowingErrors(this.updatePicturesGql)({
            filter: this.filters.pictureFilter(),
            [property]: value,
        }).then(({batchUpdatePictures: count}) => count)

    protected override _fetchList = ({skip, take}: {skip: number; take: number}) =>
        this.fetchRequest
            .fetch({
                take,
                skip,
                filter: this.filters.pictureFilter(),
                orderBy:
                    this.$selectedTab() === "projects"
                        ? [
                              // {key: PictureOrderByCriteria.SetNumber, direction: SortOrder.Asc},
                              {key: PictureOrderByCriteria.Number, direction: SortOrder.Asc},
                              {key: PictureOrderByCriteria.Id, direction: SortOrder.Desc},
                          ]
                        : [
                              {key: PictureOrderByCriteria.PriorityOrder, direction: SortOrder.Asc},
                              {key: PictureOrderByCriteria.Id, direction: SortOrder.Desc},
                          ],
            })
            .then(({pictures, picturesCount}) => ({items: pictures, totalCount: picturesCount}))

    protected override _refreshItem = ({id, legacyId}: {id?: string; legacyId?: number}) =>
        fetchThrowingErrors(this.itemsGql)({
            take: 1,
            filter: {
                ...this.filters.pictureFilter(),
                id: id ? {equals: id} : undefined,
                legacyId: legacyId ? {equals: legacyId} : undefined,
            },
        }).then(({pictures}) => pictures?.[0] || undefined)

    protected override _updateItem = (data: MutationUpdatePictureInput) =>
        mutateThrowingErrors(this.updatePictureGql)({input: data}).then(({updatePicture}) => updatePicture)

    removePicture(selectedPicture: PicturesGridItemFragment) {
        this.$items.update((items) => items.filter((item) => item.data?.id !== selectedPicture.id))
    }

    async renamePicture(picture: PicturesGridItemFragment) {
        const dialogRef = this.dialog.open(DialogRenameComponent, {
            disableClose: false,
            width: "400px",
            data: {
                title: picture.name,
            },
        })
        outputToObservable(dialogRef.componentInstance.onConfirm)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(async (name: string) =>
                this.notifications.withUserFeedback(
                    async () => {
                        await mutateThrowingErrors(this.updatePictureGql)({
                            input: {
                                id: picture.id,
                                name,
                            },
                        })
                        this.refresh.item(picture)
                    },
                    {
                        success: "Changes saved.",
                        error: "Cannot rename picture.",
                    },
                ),
            )
    }

    /**
     * Rename the picture's thumbnail
     */
    async renameDataObject(picture: PicturesGridItemFragment) {
        const latestRevisionId = picture.latestRevision?.id
        if (!latestRevisionId) {
            throw new Error("No latest revision found for picture")
        }
        const {
            pictureRevision: {pictureData},
        } = await fetchThrowingErrors(this.pictureDataGql)({id: latestRevisionId})
        const dataObject = pictureData?.[0]?.dataObject
        if (dataObject) {
            const dialogRef = this.dialog.open(DialogRenameComponent, {
                disableClose: false,
                width: "400px",
                data: {
                    title: dataObject.originalFileName,
                },
            })
            outputToObservable(dialogRef.componentInstance.onConfirm)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe(async (title: string) =>
                    this.notifications.withUserFeedback(
                        async () => {
                            await mutateThrowingErrors(this.updateDataObjectGql)({
                                input: {
                                    id: dataObject.id,
                                    originalFileName: title,
                                },
                            })
                            this.refresh.item(picture)
                        },
                        {
                            success: "Changes saved.",
                            error: "Cannot rename file.",
                        },
                    ),
                )
        }
    }

    copyTemplate(picture: PicturesGridItemFragment, setId: string | null = null): void {
        const getSetId = () => {
            if (setId !== null) return of(setId)

            const dialogRef = this.dialog.open(MovePictureDialogComponent, {
                disableClose: false,
                width: "400px",
                data: {
                    organizationId: picture.set.project.organization.id,
                    setId: picture.set.id,
                    action: Action.COPY_SCENE,
                },
            })

            return outputToObservable(dialogRef.componentInstance.onConfirm).pipe(
                takeUntilDestroyed(this.destroyRef),
                map((result) => result.setId),
            )
        }

        getSetId().subscribe(async (setId) =>
            this.notifications.withUserFeedback(
                async () => {
                    if (!setId) {
                        throw Error("No set id provided.")
                    }
                    const newPicture = await this.copyTemplateToSet(picture, setId)
                    this.refresh.item(newPicture)
                },
                {
                    success: "Picture copied.",
                    error: "Cannot copy picture.",
                },
            ),
        )
    }

    private async copyTemplateToSet(picture: PicturesGridItemFragment, setId: string) {
        const {createPicture: createdPicture} = await mutateThrowingErrors(this.createPictureGql)({
            input: {
                setId,
                nextActor: picture.nextActor,
                paymentState: PaymentState.OrderPlaced,
                state: PictureState.Draft,
            },
        })

        const {createTemplate: createdTemplate} = await mutateThrowingErrors(this.createTemplateGql)({
            input: {
                organizationId: createdPicture.organization.id,
                state: TemplateState.Draft,
                templateType: TemplateType.Picture,
                pictureId: createdPicture.id,
            },
        })

        if (picture.template) {
            const {template} = await fetchThrowingErrors(this.templateWithLatestRevisionGql)({id: picture.template?.id})
            if (template.latestRevision) {
                await mutateThrowingErrors(this.createTemplateRevisionGql)({
                    input: {
                        completed: true,
                        graph: template.latestRevision.graph,
                        templateId: createdTemplate.id,
                    },
                })
            }
        }

        return createdPicture
    }

    movePicture(picture: PicturesGridItemFragment): void {
        const dialogRef = this.dialog.open(MovePictureDialogComponent, {
            disableClose: false,
            width: "400px",
            data: {
                organizationId: picture.set.project.organization.id,
                setId: picture.set.id,
                action: Action.MOVE_PICTURE,
            },
        })

        outputToObservable(dialogRef.componentInstance.onConfirm)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(async (result: MoveCopyPictureDialogResult) =>
                this.notifications.withUserFeedback(
                    async () => {
                        if (result.setId === picture.set.id) {
                            return
                        }
                        const {updatePicture: newPicture} = await mutateThrowingErrors(this.updatePictureGql)({
                            input: {
                                id: picture.id,
                                regenerateNumber: true,
                                setId: result.setId,
                            },
                        })
                        this.refresh.item(newPicture)
                    },
                    {
                        success: "Picture moved.",
                        error: "Cannot move picture.",
                    },
                ),
            )
    }

    async deletePicture(picture: PicturesGridItemFragment) {
        const dialogRef: MatDialogRef<DialogComponent> = this.dialog.open(DialogComponent, {
            disableClose: false,
            width: "400px",
            data: {
                title: "Delete picture",
                message:
                    "Picture " +
                    picture.number +
                    " including the scene / template, all the revisions, comments and files will be deleted. " +
                    "This action <strong>cannot be undone</strong>.<br><br>Are you sure you want to continue?",
                confirmLabel: "Delete picture",
                cancelLabel: "Cancel",
            },
        })
        await this.notifications.withUserFeedback(
            async () => {
                const confirmed = await firstValueFrom(dialogRef.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)))
                if (confirmed) {
                    await mutateThrowingErrors(this.deletePictureGql)({
                        id: picture.id,
                    })
                    const picturePollingSubscription = this.dataObjectUpdateSubscriptionsMap.get(picture.id)
                    if (picturePollingSubscription && !picturePollingSubscription.closed) {
                        picturePollingSubscription.unsubscribe()
                    }
                    this.refresh.item(picture)
                }
            },
            {
                success: "Picture deleted.",
                error: "Cannot delete picture.",
            },
        )
    }

    async setHighestPriority(picture: PicturesGridItemFragment) {
        if (this.$selectedTab() !== "projects") {
            const index = this.$items().findIndex((item) => item.data?.id === picture.id)
            if (index === -1) return

            this.$items.update((items) => {
                const item = items[index]
                const newItems = [...items]
                newItems.splice(index, 1)
                newItems.splice(0, 0, {trackId: item.trackId, data: picture, placeholder: false, error: null})
                return newItems
            })
        }

        return this.updateItem({id: picture.id, priorityOrder: 0})
    }

    async dropZoneUploadSave(picture: PicturesGridItemFragment, dataObject: UploadServiceDataObjectFragment) {
        return this.notifications.withUserFeedback(
            async () => {
                await mutateThrowingErrors(this.createPictureRevisionGql)({
                    input: {
                        pictureId: picture.id,
                        dataObjectAssignment: {
                            dataObjectId: dataObject.id,
                            type: DataObjectAssignmentType.PictureData,
                        },
                    },
                })
                await this.refreshItem(picture)
            },
            {
                error: "Cannot create picture revision with data object assignment.",
            },
        )
    }

    createLinkToEditor(picture: PicturesGridItemFragment): string[] {
        if (picture.template?.latestRevision) return createLinkToEditorFromPictures(picture.id, picture.template.latestRevision.id)
        else return createLinkAddTemplateRevisionFromPictures(picture.id)
    }

    cardLink(picture: PicturesGridItemFragment) {
        if (this.organizations.$current()?.type === OrganizationType.EnterpriseCloud) {
            return this.createLinkToEditor(picture)
        }

        return [picture.id, "revisions", picture.latestRevision?.number ?? "new"]
    }

    showInProgressOverlay(picture: PicturesGridItemFragment) {
        return (
            !this.silo.$silo()?.systemRole &&
            picture.nextActor !== Enums.NextActor.Customer &&
            (picture.state === Enums.PictureState.Stage1 || picture.state == Enums.PictureState.Stage2 || picture.state == Enums.PictureState.FinalRender)
        )
    }

    thumbnailIds(picture: PicturesGridItemFragment): string[] | null {
        const pictureThumbnailId = picture.galleryImage?.id
        if (pictureThumbnailId) {
            return [pictureThumbnailId]
        } else {
            return picture.template?.latestRevision?.renderedImages?.map(({id}) => id) ?? null
        }
    }

    public getTags(picture: PicturesGridItemFragment, tagType?: TagType) {
        return picture.tagAssignments.filter((assignment) => (tagType ? assignment.tag.type === tagType : true)).map((assignment) => assignment.tag)
    }
}
